import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    Star, 
    Plus, 
    Eye, 
    Edit,
    Trash2,
    BarChart3,
    Filter,
    Ship,
    MapPin,
    Download,
    AlertTriangle,
    CheckCircle,
    XCircle,
    TrendingUp,
    Award
} from 'lucide-react';
import { toast } from 'sonner';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    RadarChart,
    PolarGrid,
    PolarAngleAxis,
    PolarRadiusAxis,
    Radar,
    LineChart,
    Line
} from 'recharts';

const API = `${window.location.origin}/api`;

const tipoOperacaoLabels = {
    RB: { label: 'RB - Recebimento Bunker', short: 'RB' },
    PA: { label: 'PA - Passagem', short: 'PA' },
    TI: { label: 'TI - Transfer Interno', short: 'TI' }
};

const classificacaoLabels = {
    excelente: { label: 'Excelente', class: 'bg-green-600', icon: Award },
    bom: { label: 'Bom', class: 'bg-blue-600', icon: CheckCircle },
    regular: { label: 'Regular', class: 'bg-yellow-600', icon: AlertTriangle },
    insatisfatorio: { label: 'Insatisfatório', class: 'bg-red-600', icon: XCircle }
};

const gradeLabels = {
    VLSFO: 'VLSFO',
    MGO: 'MGO',
    VLSFO_LV: 'VLSFO LV',
    MGO_LV: 'MGO LV'
};

const notasLabels = {
    4: { label: 'Excelente', class: 'bg-green-600' },
    3: { label: 'Bom', class: 'bg-blue-600' },
    2: { label: 'Regular', class: 'bg-yellow-600' },
    1: { label: 'Insatisfatório', class: 'bg-red-600' }
};

const criteriosLabels = {
    comunicacao: 'Comunicação',
    sms: 'SMS (Saúde, Meio Ambiente, Segurança)',
    medicao: 'Medição',
    amostragem: 'Amostragem',
    limpeza: 'Limpeza',
    avaliacao_global: 'Avaliação Global'
};

const AvaliacaoBunkerPage = () => {
    const [avaliacoes, setAvaliacoes] = useState([]);
    const [dashboard, setDashboard] = useState(null);
    const [empurradores, setEmpurradores] = useState([]);
    const [balsas, setBalsas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedAvaliacao, setSelectedAvaliacao] = useState(null);
    const [activeTab, setActiveTab] = useState('lista');
    const { hasRole } = useAuth();

    const [filters, setFilters] = useState({
        embarcacao_id: '',
        porto: '',
        navio: '',
        grade_produto: '',
        tipo_operacao: '',
        classificacao: '',
        data_inicio: '',
        data_fim: ''
    });

    const [formData, setFormData] = useState({
        tipo_operacao: 'RB',
        porto: '',
        data_operacao: new Date().toISOString().split('T')[0],
        embarcacao_tipo: 'balsa',
        embarcacao_id: '',
        navio_atendido: '',
        navio_imo: '',
        grade_produto: 'VLSFO',
        volume_operado: '',
        avaliacoes: [
            { criterio: 'comunicacao', nota: 3, observacao: '' },
            { criterio: 'sms', nota: 3, observacao: '' },
            { criterio: 'medicao', nota: 3, observacao: '' },
            { criterio: 'amostragem', nota: 3, observacao: '' },
            { criterio: 'limpeza', nota: 3, observacao: '' },
            { criterio: 'avaliacao_global', nota: 3, observacao: '' }
        ],
        observacoes: ''
    });

    useEffect(() => {
        fetchBaseData();
    }, []);

    useEffect(() => {
        fetchAvaliacoes();
        fetchDashboard();
    }, [filters]);

    const fetchBaseData = async () => {
        try {
            const [empRes, balsasRes] = await Promise.all([
                axios.get(`${API}/empurradores`),
                axios.get(`${API}/balsas`)
            ]);
            setEmpurradores(empRes.data);
            setBalsas(balsasRes.data);
        } catch (error) {
            console.error('Erro ao carregar dados base:', error);
        }
    };

    const fetchAvaliacoes = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.embarcacao_id) params.append('embarcacao_id', filters.embarcacao_id);
            if (filters.porto) params.append('porto', filters.porto);
            if (filters.navio) params.append('navio', filters.navio);
            if (filters.grade_produto) params.append('grade_produto', filters.grade_produto);
            if (filters.tipo_operacao) params.append('tipo_operacao', filters.tipo_operacao);
            if (filters.classificacao) params.append('classificacao', filters.classificacao);
            if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
            if (filters.data_fim) params.append('data_fim', filters.data_fim);

            const res = await axios.get(`${API}/avaliacoes-bunker?${params.toString()}`);
            setAvaliacoes(res.data);
        } catch (error) {
            toast.error('Erro ao carregar avaliações');
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboard = async () => {
        try {
            const params = new URLSearchParams();
            if (filters.embarcacao_id) params.append('embarcacao_id', filters.embarcacao_id);
            if (filters.porto) params.append('porto', filters.porto);
            if (filters.grade_produto) params.append('grade_produto', filters.grade_produto);
            if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
            if (filters.data_fim) params.append('data_fim', filters.data_fim);
            
            const res = await axios.get(`${API}/avaliacoes-bunker/dashboard?${params.toString()}`);
            setDashboard(res.data);
        } catch (error) {
            console.error('Erro ao carregar dashboard:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.embarcacao_id) {
            toast.error('Selecione a embarcação');
            return;
        }
        try {
            const payload = {
                ...formData,
                volume_operado: formData.volume_operado ? parseFloat(formData.volume_operado) : null
            };

            if (selectedAvaliacao) {
                await axios.put(`${API}/avaliacoes-bunker/${selectedAvaliacao.id}`, payload);
                toast.success('Avaliação atualizada com sucesso');
            } else {
                await axios.post(`${API}/avaliacoes-bunker`, payload);
                toast.success('Avaliação registrada com sucesso');
            }

            setDialogOpen(false);
            resetForm();
            fetchAvaliacoes();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar avaliação');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Deseja realmente excluir esta avaliação?')) return;
        
        try {
            await axios.delete(`${API}/avaliacoes-bunker/${id}`);
            toast.success('Avaliação excluída');
            fetchAvaliacoes();
            fetchDashboard();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir');
        }
    };

    const handleExport = async (formato) => {
        try {
            const params = new URLSearchParams();
            params.append('formato', formato);
            if (filters.embarcacao_id) params.append('embarcacao_id', filters.embarcacao_id);
            if (filters.porto) params.append('porto', filters.porto);
            if (filters.data_inicio) params.append('data_inicio', filters.data_inicio);
            if (filters.data_fim) params.append('data_fim', filters.data_fim);

            const res = await axios.get(`${API}/avaliacoes-bunker/relatorio/exportar?${params.toString()}`);
            
            if (formato === 'csv' && res.data.csv_content) {
                const blob = new Blob([res.data.csv_content], { type: 'text/csv;charset=utf-8;' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `avaliacoes_bunker_${new Date().toISOString().split('T')[0]}.csv`;
                link.click();
                toast.success('Arquivo CSV exportado');
            } else {
                // JSON
                const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
                const link = document.createElement('a');
                link.href = URL.createObjectURL(blob);
                link.download = `avaliacoes_bunker_${new Date().toISOString().split('T')[0]}.json`;
                link.click();
                toast.success('Arquivo JSON exportado');
            }
        } catch (error) {
            toast.error('Erro ao exportar');
        }
    };

    const resetForm = () => {
        setSelectedAvaliacao(null);
        setFormData({
            tipo_operacao: 'RB',
            porto: '',
            data_operacao: new Date().toISOString().split('T')[0],
            embarcacao_tipo: 'balsa',
            embarcacao_id: '',
            navio_atendido: '',
            navio_imo: '',
            grade_produto: 'VLSFO',
            volume_operado: '',
            avaliacoes: [
                { criterio: 'comunicacao', nota: 3, observacao: '' },
                { criterio: 'sms', nota: 3, observacao: '' },
                { criterio: 'medicao', nota: 3, observacao: '' },
                { criterio: 'amostragem', nota: 3, observacao: '' },
                { criterio: 'limpeza', nota: 3, observacao: '' },
                { criterio: 'avaliacao_global', nota: 3, observacao: '' }
            ],
            observacoes: ''
        });
    };

    const openEditDialog = (avaliacao) => {
        setSelectedAvaliacao(avaliacao);
        setFormData({
            tipo_operacao: avaliacao.tipo_operacao,
            porto: avaliacao.porto,
            data_operacao: avaliacao.data_operacao,
            embarcacao_tipo: avaliacao.embarcacao_tipo,
            embarcacao_id: avaliacao.embarcacao_id,
            navio_atendido: avaliacao.navio_atendido,
            navio_imo: avaliacao.navio_imo || '',
            grade_produto: avaliacao.grade_produto,
            volume_operado: avaliacao.volume_operado || '',
            avaliacoes: avaliacao.avaliacoes || [],
            observacoes: avaliacao.observacoes || ''
        });
        setDialogOpen(true);
    };

    const updateNota = (criterio, nota) => {
        setFormData(prev => ({
            ...prev,
            avaliacoes: prev.avaliacoes.map(av => 
                av.criterio === criterio ? { ...av, nota: parseInt(nota) } : av
            )
        }));
    };

    const updateObservacaoCriterio = (criterio, observacao) => {
        setFormData(prev => ({
            ...prev,
            avaliacoes: prev.avaliacoes.map(av => 
                av.criterio === criterio ? { ...av, observacao } : av
            )
        }));
    };

    const getEmbarcacoes = () => {
        return formData.embarcacao_tipo === 'empurrador' ? empurradores : balsas;
    };

    const clearFilters = () => {
        setFilters({
            embarcacao_id: '',
            porto: '',
            navio: '',
            grade_produto: '',
            tipo_operacao: '',
            classificacao: '',
            data_inicio: '',
            data_fim: ''
        });
    };

    // Preparar dados para gráficos
    const radarData = dashboard?.por_criterio ? 
        Object.keys(dashboard.por_criterio).map(criterio => ({
            criterio: criteriosLabels[criterio] || criterio,
            media: dashboard.por_criterio[criterio].media,
            fullMark: 4
        })) : [];

    const barChartData = dashboard?.por_classificacao ? [
        { name: 'Excelente', valor: dashboard.por_classificacao.excelente || 0, fill: '#22c55e' },
        { name: 'Bom', valor: dashboard.por_classificacao.bom || 0, fill: '#3b82f6' },
        { name: 'Regular', valor: dashboard.por_classificacao.regular || 0, fill: '#eab308' },
        { name: 'Insatisfatório', valor: dashboard.por_classificacao.insatisfatorio || 0, fill: '#ef4444' }
    ] : [];

    return (
        <div className="space-y-6" data-testid="avaliacao-bunker-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-white">Avaliação de Fornecimento de Bunker</h1>
                    <p className="text-slate-400 text-sm">Baseado na norma Petrobras N-2652F-F01</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleExport('csv')}
                        data-testid="export-csv-btn"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        CSV
                    </Button>
                    {(hasRole(['admin', 'gestor', 'operador'])) && (
                        <Button 
                            onClick={() => { resetForm(); setDialogOpen(true); }}
                            data-testid="new-avaliacao-btn"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Nova Avaliação
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-700 pb-2">
                <Button
                    variant={activeTab === 'lista' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('lista')}
                    data-testid="tab-lista"
                >
                    <Star className="w-4 h-4 mr-2" />
                    Avaliações
                </Button>
                <Button
                    variant={activeTab === 'dashboard' ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setActiveTab('dashboard')}
                    data-testid="tab-dashboard"
                >
                    <BarChart3 className="w-4 h-4 mr-2" />
                    Dashboard
                </Button>
            </div>

            {/* Filtros */}
            <Card className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                        <Filter className="w-4 h-4" />
                        Filtros
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                        <div>
                            <Label className="text-xs">Embarcação</Label>
                            <Select
                                value={filters.embarcacao_id || "_all"}
                                onValueChange={(v) => setFilters(p => ({ ...p, embarcacao_id: v === "_all" ? "" : v }))}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Todas" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_all">Todas</SelectItem>
                                    {balsas.length > 0 && <SelectItem value="_lbl_balsas" disabled className="text-xs font-semibold text-cyan-500 opacity-100">— Balsas —</SelectItem>}
                                    {balsas.map(b => (
                                        <SelectItem key={b.id} value={b.id}>{b.nome}</SelectItem>
                                    ))}
                                    {empurradores.length > 0 && <SelectItem value="_lbl_emp" disabled className="text-xs font-semibold text-cyan-500 opacity-100">— Empurradores —</SelectItem>}
                                    {empurradores.map(e => (
                                        <SelectItem key={e.id} value={e.id}>{e.nome}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Porto</Label>
                            <Input
                                value={filters.porto}
                                onChange={(e) => setFilters(p => ({ ...p, porto: e.target.value }))}
                                placeholder="Buscar..."
                                className="h-9"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Navio</Label>
                            <Input
                                value={filters.navio}
                                onChange={(e) => setFilters(p => ({ ...p, navio: e.target.value }))}
                                placeholder="Buscar..."
                                className="h-9"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Produto</Label>
                            <Select
                                value={filters.grade_produto || "_all"}
                                onValueChange={(v) => setFilters(p => ({ ...p, grade_produto: v === "_all" ? "" : v }))}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Todos" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_all">Todos</SelectItem>
                                    {Object.entries(gradeLabels).map(([key, label]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Classificação</Label>
                            <Select
                                value={filters.classificacao || "_all"}
                                onValueChange={(v) => setFilters(p => ({ ...p, classificacao: v === "_all" ? "" : v }))}
                            >
                                <SelectTrigger className="h-9">
                                    <SelectValue placeholder="Todas" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="_all">Todas</SelectItem>
                                    {Object.entries(classificacaoLabels).map(([key, data]) => (
                                        <SelectItem key={key} value={key}>{data.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs">Data Início</Label>
                            <Input
                                type="date"
                                value={filters.data_inicio}
                                onChange={(e) => setFilters(p => ({ ...p, data_inicio: e.target.value }))}
                                className="h-9"
                            />
                        </div>
                        <div>
                            <Label className="text-xs">Data Fim</Label>
                            <Input
                                type="date"
                                value={filters.data_fim}
                                onChange={(e) => setFilters(p => ({ ...p, data_fim: e.target.value }))}
                                className="h-9"
                            />
                        </div>
                        <div className="flex items-end">
                            <Button variant="outline" size="sm" onClick={clearFilters} className="h-9 w-full">
                                Limpar
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Conteúdo baseado na tab ativa */}
            {activeTab === 'lista' ? (
                /* Lista de Avaliações */
                <Card className="bg-slate-900 border-slate-800">
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="border-slate-800">
                                        <TableHead>Data</TableHead>
                                        <TableHead>Tipo</TableHead>
                                        <TableHead>Porto</TableHead>
                                        <TableHead>Navio</TableHead>
                                        <TableHead>Embarcação</TableHead>
                                        <TableHead>Produto</TableHead>
                                        <TableHead className="text-center">Média</TableHead>
                                        <TableHead className="text-center">Classificação</TableHead>
                                        <TableHead className="text-right">Ações</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                                                Carregando...
                                            </TableCell>
                                        </TableRow>
                                    ) : avaliacoes.length === 0 ? (
                                        <TableRow>
                                            <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                                                Nenhuma avaliação encontrada
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        avaliacoes.map((av) => {
                                            const classif = classificacaoLabels[av.classificacao] || classificacaoLabels.regular;
                                            const ClassifIcon = classif.icon;
                                            return (
                                                <TableRow key={av.id} className="border-slate-800 hover:bg-slate-800/50">
                                                    <TableCell className="font-mono text-sm">
                                                        {av.data_operacao}
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="px-2 py-0.5 rounded text-xs bg-slate-700">
                                                            {tipoOperacaoLabels[av.tipo_operacao]?.short || av.tipo_operacao}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell>{av.porto}</TableCell>
                                                    <TableCell>{av.navio_atendido}</TableCell>
                                                    <TableCell>
                                                        <div className="flex items-center gap-1">
                                                            <Ship className="w-3 h-3 text-slate-400" />
                                                            {av.embarcacao_nome}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <span className="px-2 py-0.5 rounded text-xs bg-cyan-600/20 text-cyan-400">
                                                            {gradeLabels[av.grade_produto] || av.grade_produto}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span className="font-bold text-lg">
                                                            {av.media_geral?.toFixed(2)}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-center">
                                                        <span className={`px-2 py-1 rounded text-xs text-white flex items-center gap-1 justify-center ${classif.class}`}>
                                                            <ClassifIcon className="w-3 h-3" />
                                                            {classif.label}
                                                        </span>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex gap-1 justify-end">
                                                            <Button
                                                                variant="ghost"
                                                                size="icon"
                                                                onClick={() => { setSelectedAvaliacao(av); setDetailDialogOpen(true); }}
                                                                data-testid={`view-${av.id}`}
                                                            >
                                                                <Eye className="w-4 h-4" />
                                                            </Button>
                                                            {hasRole(['admin', 'gestor']) && (
                                                                <>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="icon"
                                                                        onClick={() => openEditDialog(av)}
                                                                        data-testid={`edit-${av.id}`}
                                                                    >
                                                                        <Edit className="w-4 h-4" />
                                                                    </Button>
                                                                    {hasRole(['admin']) && (
                                                                        <Button
                                                                            variant="ghost"
                                                                            size="icon"
                                                                            className="text-red-400 hover:text-red-300"
                                                                            onClick={() => handleDelete(av.id)}
                                                                            data-testid={`delete-${av.id}`}
                                                                        >
                                                                            <Trash2 className="w-4 h-4" />
                                                                        </Button>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                /* Dashboard */
                <div className="space-y-6">
                    {/* KPIs */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="pt-4">
                                <div className="text-slate-400 text-sm">Total Avaliações</div>
                                <div className="text-3xl font-bold text-white">{dashboard?.total_avaliacoes || 0}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="pt-4">
                                <div className="text-slate-400 text-sm">Média Geral</div>
                                <div className="text-3xl font-bold text-cyan-400">{dashboard?.media_geral?.toFixed(2) || '0.00'}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="pt-4">
                                <div className="text-slate-400 text-sm">Classificação Geral</div>
                                <div className={`text-xl font-bold ${
                                    dashboard?.classificacao_geral === 'excelente' ? 'text-green-400' :
                                    dashboard?.classificacao_geral === 'bom' ? 'text-blue-400' :
                                    dashboard?.classificacao_geral === 'regular' ? 'text-yellow-400' : 'text-red-400'
                                }`}>
                                    {classificacaoLabels[dashboard?.classificacao_geral]?.label || 'N/A'}
                                </div>
                            </CardContent>
                        </Card>
                        <Card className="bg-slate-900 border-slate-800">
                            <CardContent className="pt-4">
                                <div className="text-slate-400 text-sm">Alertas</div>
                                <div className="text-3xl font-bold text-red-400">{dashboard?.alertas?.length || 0}</div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Gráficos */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Gráfico Radar - Por Critério */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm">Média por Critério</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <RadarChart data={radarData}>
                                            <PolarGrid stroke="#334155" />
                                            <PolarAngleAxis dataKey="criterio" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                            <PolarRadiusAxis domain={[0, 4]} tick={{ fill: '#94a3b8' }} />
                                            <Radar
                                                name="Média"
                                                dataKey="media"
                                                stroke="#22d3ee"
                                                fill="#22d3ee"
                                                fillOpacity={0.3}
                                            />
                                            <Tooltip 
                                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                                labelStyle={{ color: '#f8fafc' }}
                                            />
                                        </RadarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Gráfico de Barras - Por Classificação */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm">Distribuição por Classificação</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <BarChart data={barChartData}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                            <XAxis dataKey="name" tick={{ fill: '#94a3b8' }} />
                                            <YAxis tick={{ fill: '#94a3b8' }} />
                                            <Tooltip 
                                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                                labelStyle={{ color: '#f8fafc' }}
                                            />
                                            <Bar dataKey="valor" radius={[4, 4, 0, 0]} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Gráfico de Tendência */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <TrendingUp className="w-4 h-4" />
                                    Tendência Mensal
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="h-80">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={dashboard?.tendencia || []}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                                            <XAxis dataKey="mes" tick={{ fill: '#94a3b8', fontSize: 10 }} />
                                            <YAxis domain={[0, 4]} tick={{ fill: '#94a3b8' }} />
                                            <Tooltip 
                                                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                                                labelStyle={{ color: '#f8fafc' }}
                                            />
                                            <Line 
                                                type="monotone" 
                                                dataKey="media" 
                                                stroke="#22d3ee" 
                                                strokeWidth={2}
                                                dot={{ fill: '#22d3ee' }}
                                            />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            </CardContent>
                        </Card>

                        {/* Ranking de Embarcações */}
                        <Card className="bg-slate-900 border-slate-800">
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-2">
                                    <Award className="w-4 h-4" />
                                    Ranking de Embarcações
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {dashboard?.por_embarcacao?.map((emb, idx) => {
                                        const classif = classificacaoLabels[emb.classificacao] || classificacaoLabels.regular;
                                        return (
                                            <div key={emb.embarcacao_id} className="flex items-center justify-between p-2 bg-slate-800 rounded">
                                                <div className="flex items-center gap-3">
                                                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                                                        idx === 0 ? 'bg-yellow-500 text-yellow-950' :
                                                        idx === 1 ? 'bg-slate-300 text-slate-800' :
                                                        idx === 2 ? 'bg-amber-600 text-amber-950' : 'bg-slate-600'
                                                    }`}>
                                                        {idx + 1}
                                                    </span>
                                                    <div>
                                                        <div className="font-medium text-white">{emb.embarcacao_nome}</div>
                                                        <div className="text-xs text-slate-400">{emb.total_avaliacoes} avaliações</div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-bold text-lg text-cyan-400">{emb.media.toFixed(2)}</div>
                                                    <span className={`px-2 py-0.5 rounded text-xs ${classif.class}`}>
                                                        {classif.label}
                                                    </span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {(!dashboard?.por_embarcacao || dashboard.por_embarcacao.length === 0) && (
                                        <div className="text-center py-4 text-slate-400">
                                            Nenhum dado disponível
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Alertas Recentes */}
                    {dashboard?.alertas && dashboard.alertas.length > 0 && (
                        <Card className="bg-slate-900 border-slate-800 border-red-900/50">
                            <CardHeader>
                                <CardTitle className="text-sm flex items-center gap-2 text-red-400">
                                    <AlertTriangle className="w-4 h-4" />
                                    Alertas Recentes (Notas Baixas)
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-2">
                                    {dashboard.alertas.map((alerta, idx) => (
                                        <div key={idx} className="flex items-center justify-between p-3 bg-red-950/30 border border-red-900/30 rounded">
                                            <div>
                                                <div className="font-medium text-white">{alerta.navio} - {alerta.porto}</div>
                                                <div className="text-sm text-slate-400">{alerta.data}</div>
                                            </div>
                                            <div className="text-right">
                                                <div className="text-red-400 font-medium">{alerta.criterio}</div>
                                                <div className="text-2xl font-bold text-red-500">{alerta.nota}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            )}

            {/* Dialog de Nova/Editar Avaliação */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-800">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedAvaliacao ? 'Editar Avaliação' : 'Nova Avaliação de Bunker'}
                        </DialogTitle>
                    </DialogHeader>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Dados da Operação */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <Label>Tipo de Operação *</Label>
                                <Select
                                    value={formData.tipo_operacao}
                                    onValueChange={(v) => setFormData(p => ({ ...p, tipo_operacao: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(tipoOperacaoLabels).map(([key, data]) => (
                                            <SelectItem key={key} value={key}>{data.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Data da Operação *</Label>
                                <Input
                                    type="date"
                                    value={formData.data_operacao}
                                    onChange={(e) => setFormData(p => ({ ...p, data_operacao: e.target.value }))}
                                    required
                                />
                            </div>
                            <div>
                                <Label>Porto *</Label>
                                <Input
                                    value={formData.porto}
                                    onChange={(e) => setFormData(p => ({ ...p, porto: e.target.value }))}
                                    placeholder="Ex: Santos, Paranaguá"
                                    required
                                />
                            </div>
                            <div>
                                <Label>Produto *</Label>
                                <Select
                                    value={formData.grade_produto}
                                    onValueChange={(v) => setFormData(p => ({ ...p, grade_produto: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(gradeLabels).map(([key, label]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* Embarcação e Navio */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                                <Label>Tipo Embarcação</Label>
                                <Select
                                    value={formData.embarcacao_tipo}
                                    onValueChange={(v) => setFormData(p => ({ ...p, embarcacao_tipo: v, embarcacao_id: '' }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="balsa">Balsa</SelectItem>
                                        <SelectItem value="empurrador">Empurrador</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Embarcação *</Label>
                                <Select
                                    value={formData.embarcacao_id}
                                    onValueChange={(v) => setFormData(p => ({ ...p, embarcacao_id: v }))}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Selecione..." />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {getEmbarcacoes().map(emb => (
                                            <SelectItem key={emb.id} value={emb.id}>{emb.nome}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Navio Atendido *</Label>
                                <Input
                                    value={formData.navio_atendido}
                                    onChange={(e) => setFormData(p => ({ ...p, navio_atendido: e.target.value }))}
                                    placeholder="Nome do navio"
                                    required
                                />
                            </div>
                            <div>
                                <Label>IMO do Navio</Label>
                                <Input
                                    value={formData.navio_imo}
                                    onChange={(e) => setFormData(p => ({ ...p, navio_imo: e.target.value }))}
                                    placeholder="IMO (opcional)"
                                />
                            </div>
                        </div>

                        <div>
                            <Label>Volume Operado (m³)</Label>
                            <Input
                                type="number"
                                step="0.01"
                                value={formData.volume_operado}
                                onChange={(e) => setFormData(p => ({ ...p, volume_operado: e.target.value }))}
                                placeholder="Volume em m³"
                            />
                        </div>

                        {/* Avaliações por Critério */}
                        <div>
                            <Label className="text-lg font-semibold mb-4 block">Avaliação por Critério</Label>
                            <p className="text-xs text-slate-400 mb-4">
                                4 = Excelente | 3 = Bom | 2 = Regular | 1 = Insatisfatório
                            </p>
                            <div className="space-y-4">
                                {formData.avaliacoes.map((av) => (
                                    <div key={av.criterio} className="p-4 bg-slate-800 rounded-lg">
                                        <div className="flex flex-col md:flex-row md:items-center gap-4">
                                            <div className="flex-1">
                                                <Label className="text-white font-medium">
                                                    {criteriosLabels[av.criterio]}
                                                </Label>
                                            </div>
                                            <div className="flex gap-2">
                                                {[1, 2, 3, 4].map((nota) => (
                                                    <Button
                                                        key={nota}
                                                        type="button"
                                                        size="sm"
                                                        variant={av.nota === nota ? 'default' : 'outline'}
                                                        className={av.nota === nota ? notasLabels[nota].class : ''}
                                                        onClick={() => updateNota(av.criterio, nota)}
                                                        data-testid={`nota-${av.criterio}-${nota}`}
                                                    >
                                                        {nota}
                                                    </Button>
                                                ))}
                                            </div>
                                            <div className="flex-1">
                                                <Input
                                                    value={av.observacao}
                                                    onChange={(e) => updateObservacaoCriterio(av.criterio, e.target.value)}
                                                    placeholder="Observação (opcional)"
                                                    className="text-sm"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Observações Gerais */}
                        <div>
                            <Label>Observações Gerais</Label>
                            <Textarea
                                value={formData.observacoes}
                                onChange={(e) => setFormData(p => ({ ...p, observacoes: e.target.value }))}
                                placeholder="Observações adicionais sobre a operação..."
                                rows={3}
                            />
                        </div>

                        <div className="flex justify-end gap-2 pt-4 border-t border-slate-700">
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancelar
                            </Button>
                            <Button type="submit" data-testid="submit-avaliacao-btn">
                                {selectedAvaliacao ? 'Salvar Alterações' : 'Registrar Avaliação'}
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Dialog de Detalhes */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="max-w-2xl bg-slate-900 border-slate-800">
                    <DialogHeader>
                        <DialogTitle>Detalhes da Avaliação</DialogTitle>
                    </DialogHeader>

                    {selectedAvaliacao && (
                        <div className="space-y-4">
                            {/* Info da Operação */}
                            <div className="grid grid-cols-2 gap-4 p-4 bg-slate-800 rounded-lg">
                                <div>
                                    <Label className="text-slate-400 text-xs">Data</Label>
                                    <div className="font-medium">{selectedAvaliacao.data_operacao}</div>
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Tipo</Label>
                                    <div className="font-medium">{tipoOperacaoLabels[selectedAvaliacao.tipo_operacao]?.label}</div>
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Porto</Label>
                                    <div className="font-medium">{selectedAvaliacao.porto}</div>
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Navio</Label>
                                    <div className="font-medium">{selectedAvaliacao.navio_atendido}</div>
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Embarcação</Label>
                                    <div className="font-medium">{selectedAvaliacao.embarcacao_nome}</div>
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Produto</Label>
                                    <div className="font-medium">{gradeLabels[selectedAvaliacao.grade_produto]}</div>
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Volume</Label>
                                    <div className="font-medium">{selectedAvaliacao.volume_operado?.toFixed(2) || 'N/A'} m³</div>
                                </div>
                                <div>
                                    <Label className="text-slate-400 text-xs">Classificação</Label>
                                    <div className={`inline-block px-2 py-1 rounded text-sm ${classificacaoLabels[selectedAvaliacao.classificacao]?.class}`}>
                                        {classificacaoLabels[selectedAvaliacao.classificacao]?.label}
                                    </div>
                                </div>
                            </div>

                            {/* Notas por Critério */}
                            <div>
                                <Label className="text-lg font-semibold mb-3 block">Notas por Critério</Label>
                                <div className="space-y-2">
                                    {selectedAvaliacao.avaliacoes?.map((av) => (
                                        <div key={av.criterio} className="flex items-center justify-between p-3 bg-slate-800 rounded">
                                            <span className="text-slate-300">{criteriosLabels[av.criterio]}</span>
                                            <div className="flex items-center gap-2">
                                                <span className={`px-3 py-1 rounded font-bold ${notasLabels[av.nota]?.class}`}>
                                                    {av.nota}
                                                </span>
                                                <span className="text-xs text-slate-400">
                                                    {notasLabels[av.nota]?.label}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Médias */}
                            <div className="grid grid-cols-2 gap-4 p-4 bg-cyan-950/30 border border-cyan-900/30 rounded-lg">
                                <div className="text-center">
                                    <div className="text-slate-400 text-sm">Média Individual</div>
                                    <div className="text-3xl font-bold text-cyan-400">
                                        {selectedAvaliacao.media_individual?.toFixed(2)}
                                    </div>
                                </div>
                                <div className="text-center">
                                    <div className="text-slate-400 text-sm">Média Geral</div>
                                    <div className="text-3xl font-bold text-cyan-400">
                                        {selectedAvaliacao.media_geral?.toFixed(2)}
                                    </div>
                                </div>
                            </div>

                            {/* Observações */}
                            {selectedAvaliacao.observacoes && (
                                <div>
                                    <Label className="text-slate-400 text-xs">Observações</Label>
                                    <div className="p-3 bg-slate-800 rounded mt-1">
                                        {selectedAvaliacao.observacoes}
                                    </div>
                                </div>
                            )}

                            {/* Metadados */}
                            <div className="text-xs text-slate-500 pt-2 border-t border-slate-700">
                                <span>Registrado por: {selectedAvaliacao.criado_por_nome}</span>
                                <span className="mx-2">|</span>
                                <span>Em: {selectedAvaliacao.criado_em?.split('T')[0]}</span>
                                {selectedAvaliacao.alerta_enviado && (
                                    <>
                                        <span className="mx-2">|</span>
                                        <span className="text-yellow-500">Alerta enviado</span>
                                    </>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AvaliacaoBunkerPage;
